
import { Component, Vue } from 'vue-property-decorator';
import { appTypeToAuthType, getLoginErrorString } from '@/services/auth';
import { RawLocation } from 'vue-router';
import { AppIds } from '@/consts';
import { EventBus } from '@/services/event-bus';
import { AuthProvider } from '@/models/authProvider';
import LoginForm from '@/components/common/LoginForm.vue';
import { ResponseError } from '@/models/responseError';

@Component({
  components: { LoginForm },
})
export default class LoginPage extends Vue {
  private showing: AuthProvider = 'coursepro'; // if we can swap between auth providers with a form.
  private loading = false;
  private redirectActiveUser = false;

  get appId() {
    return this.$store.getters.appId as AppIds;
  }

  get title() {
    const portal = this.$t(`login.${this.appId}_portal`);
    return this.$t('login.log_in_to', { portal });
  }

  get clientAuthProvider() {
    const client = this.$store.getters['auth/clientDetails'] || {};
    return client.auth_provider || 'local';
  }

  get forceAllowEmailLogin() {
    return 'allow_email_login' in this.$route.query;
  }

  available(provider: AuthProvider) {
    return this.availableProviders.includes(provider);
  }

  get availableProviders() {
    // login providers that are valid for this portal + client
    if (this.clientAuthProvider === 'coursepro') {
      if (this.appId === 'club' || this.forceAllowEmailLogin) {
        // allow local logins for activity portal
        return ['coursepro', 'local'];
      }
      return ['coursepro'];
    }

    if (this.clientAuthProvider === 'bg') {
      if (this.appId === 'scheme') {
        return ['local'];
      }
      return this.appId === 'parent' || this.forceAllowEmailLogin ? ['bg', 'local'] : ['bg'];
    }

    // local-only
    return ['local'];
  }

  async created() {
    if (this.$route.query?.redirectActive) {
      this.$alert('error', this.$t('login.account_already_activated'));
    }

    // Check if auth has already initialized
    // If it isn't then we wait for the 'user-initialized' event before handling the page load
    const initialized = this.$store.getters['auth/initialized'];
    if (initialized) {
      await this.handlePageLoad();
    } else {
      EventBus.$once('user-initialized', async () => {
        await this.handlePageLoad();
      });
    }
  }

  async handlePageLoad() {
    if (this.$route.query.token && this.$route.query.provider) {
      const ngoId = typeof this.$route.query.club_ngo_id === 'string' ? this.$route.query.club_ngo_id : '';
      const provider = typeof this.$route.query.provider === 'string' ? this.$route.query.provider : '';
      const token = typeof this.$route.query.token === 'string' ? this.$route.query.token : '';
      await this.autoLogin(provider, token, ngoId);
    } else if (this.$store.getters['auth/isLoggedIn']()) {
      this.gotoNextPage();
    } else {
      await this.$store.dispatch('auth/logout');
    }
  }

  async autoLogin(provider: string, token: string, clubNgoId: string) {
    this.loading = true;
    const client = this.$store.getters['auth/clientDetails'] || {};
    const authType = appTypeToAuthType(this.$store.getters.appId);
    const ok = await this.$store.dispatch('auth/login', {
      clientId: client.id,
      authType,
      provider,
      extra: { token, club_ngo_id: clubNgoId },
    });
    if (ok === true) {
      this.gotoNextPage();
    } else {
      if (ok === false) {
        // Show password reset alert for 30 secs
        this.$alert('error', this.$t('login.errors.PASSWORD_RESET_REQUIRED'), 30000);
      } else {
        this.$alert('error', getLoginErrorString(ok as ResponseError));
      }
    }
  }

  gotoNextPage() {
    const query = this.$route.query;
    if (query.scheme_slug) {
      if (query.session_plan_group_uuid) {
        this.$store.commit('planning/updateTab', 1);
        const to = {
          name: 'lesson-plan-group',
          params: {
            schemeSlug: `${query.scheme_slug}`,
            lessonPlanGroupUuid: `${query.session_plan_group_uuid}`,
          },
        };
        this.$router.push(to);
      } else {
        this.$router.push(`/overview?scheme_slug=${query.scheme_slug}`);
      }
    } else if (query.session_planning) {
      this.$store.commit('planning/updateTab', 1);
      this.$router.push({ name: 'planning' });
    } else if (this.$store.getters['auth/authProvider'] === 'coursepro' && this.$store.getters.appId === 'club') {
      this.$router.push('/sessions');
    } else {
      this.$router.push('/');
    }
    this.loading = false;
  }

  async signin(provider: AuthProvider, user: { email: string; password: string }) {
    if (user.email === '' || user.password === '') {
      this.$alert('error', this.$t('login.missing_email_or_password'));
      return;
    }
    this.loading = true;
    const client = this.$store.getters['auth/clientDetails'] || {};
    const authType = appTypeToAuthType(this.$store.getters.appId);
    const extra = user;
    const ok = await this.$store.dispatch('auth/login', {
      clientId: client.id,
      authType,
      provider,
      extra,
    });
    if (ok === true) {
      const returnUrl = this.$route.query.returnUrl;
      // return to where they wanted originally
      if (returnUrl) {
        if (returnUrl.indexOf('login') > -1 || returnUrl.indexOf('returnUrl') > -1) {
          this.$router.push('/');
        } else {
          this.$router.push(this.$route.query.returnUrl as RawLocation);
        }
      } else {
        // go to home page
        this.$router.push('/');
      }
    } else {
      if (ok === false) {
        this.$alert('error', this.$t('login.errors.PASSWORD_RESET_REQUIRED'));
      } else {
        this.$alert('error', getLoginErrorString(ok as ResponseError));
      }
    }
    this.loading = false;
  }

  async loginWithNgo() {
    try {
      const ngoAuthUrl: string = await this.$store.dispatch('auth/getNgoAuthUrl');
      window.location.replace(ngoAuthUrl);
    } catch {
      this.$alert('error', this.$t('login.error_ngo_authurl'));
    }
  }
}
